function toDate (value) {
  if (value && typeof value.toDate === "function") return value.toDate();
  return new Date(value);
}

function dateKey (date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function sundayBefore (date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function nthOccurrence (sundayAlignedStart, slot, n, blackoutKeys) {
  const date = new Date(sundayAlignedStart);
  date.setDate(date.getDate() + (n - 1) * 7 + slot.dayOfWeek);
  if (blackoutKeys.has(dateKey(date))) return null;
  date.setHours(Math.floor(slot.time / 60), slot.time % 60, 0, 0);
  return date;
}

function groupByWeek (races) {
  const weeks = new Map();
  for (const race of races) {
    if (!weeks.has(race.week)) weeks.set(race.week, []);
    weeks.get(race.week).push(race);
  }
  return weeks;
}

// Checks the single-elimination bracket: every bracket game whose finalists are both known
// should be hosted at its higher seed's home venue, un-bumped. Returns a single check result
// whose `data` lists every bump (slot, expected vs actual venue, and squad titles). Slots still
// TBD (finalists not both decided) are not violations.
export function checkBracketSchedule (league) {
  const slotSquads = league.bracketSquads || {};
  const scheduled = league.bracketSchedule || {};
  const squadById = new Map(league.squads.map(s => [s.id, s]));
  const locationById = new Map((league.locations || []).map(l => [l.id, l]));
  const nameOf = locId => (locId && locationById.get(locId)?.name) || locId || null;

  const bumps = [];
  for (const [slotId, ids] of Object.entries(slotSquads)) {
    const homeId = ids && ids[0];
    const awayId = ids && ids[1];
    if (!homeId || !awayId) continue;
    const entry = scheduled[slotId];
    if (!entry || !entry.bumped) continue;
    const home = squadById.get(homeId);
    const away = squadById.get(awayId);
    bumps.push({
      slotId,
      homeSquad: home ? home.title : homeId,
      awaySquad: away ? away.title : awayId,
      expectedLocationId: home ? home.locationId : null,
      expectedLocation: home ? nameOf(home.locationId) : null,
      actualLocationId: entry.locationId,
      actualLocation: nameOf(entry.locationId)
    });
  }

  return {
    key: "bracket",
    label: "Every bracket game is hosted at its higher seed's home venue.",
    passed: bumps.length === 0,
    data: bumps
  };
}

// Walks a schedule and returns one check result per rule:
//  - hard constraints every schedule must satisfy (1-7),
//  - soft objectives the scheduler optimises (8-9), which can legitimately be red on a
//    valid-but-constrained schedule (e.g. a venue with one table hosting many squads).
export function checkLeagueSchedule (league, races) {
  const squads = league.squads;
  const locations = league.locations || [];
  const times = league.timesEachPairPlays;
  const results = [];
  races = (races || []).filter(r => !r.bracketSlot && !r.isBlackout);

  let pairsComplete = true;
  const pairCounts = new Map();
  for (const race of races) {
    const key = [race.homeSquadId, race.awaySquadId].sort().join("|");
    pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
  }
  const expectedPairs = squads.length * (squads.length - 1) / 2;
  if (pairCounts.size !== expectedPairs || [...pairCounts.values()].some(count => count !== times)) {
    pairsComplete = false;
  }
  results.push({
    key: "pairs",
    label: `Every pair of teams plays exactly ${times} time${times === 1 ? "" : "s"}.`,
    passed: pairsComplete
  });

  let spacingOk = true;
  {
    const possiblePairs = squads.length * (squads.length - 1) / 2;
    if (possiblePairs > 1) {
      const byWeek = groupByWeek(races);
      const weekNums = [...byWeek.keys()].sort((a, b) => a - b);
      for (let i = 1; i < weekNums.length; i++) {
        const prevPairs = new Set();
        for (const race of byWeek.get(weekNums[i - 1])) {
          prevPairs.add([race.homeSquadId, race.awaySquadId].sort().join("|"));
        }
        for (const race of byWeek.get(weekNums[i])) {
          if (prevPairs.has([race.homeSquadId, race.awaySquadId].sort().join("|"))) {
            spacingOk = false;
            break;
          }
        }
        if (!spacingOk) break;
      }
    }
  }
  results.push({
    key: "spacing",
    label: "No team plays the same opponent in consecutive weeks.",
    passed: spacingOk
  });

  const weekNumbers = [...new Set(races.map(race => race.week))].sort((a, b) => a - b);
  const firstWeek = weekNumbers[0] || 1;
  results.push({
    key: "weeks",
    label: "Weeks run consecutively with no gaps.",
    passed: weekNumbers.length === 0 || weekNumbers.every((week, index) => week === firstWeek + index)
  });

  const squadById = new Map(squads.map(squad => [squad.id, squad]));
  const locationById = new Map(locations.map(location => [location.id, location]));
  const startDate = toDate(league.startDate);
  const blackoutKeys = new Set((league.blackoutDates || []).map(d => (typeof d === "string" ? d : dateKey(toDate(d)))));
  const sundayAlignedStart = sundayBefore(startDate);

  let pairedUpOk = true;
  let tablesOk = true;
  let datesOk = true;
  let perWeekdayRuleOk = true;
  let venueUsageOk = true;

  const weeks = groupByWeek(races);
  for (const weekRaces of weeks.values()) {
    const tableSlots = new Set();
    const squadGames = new Map();
    for (const race of weekRaces) {
      const home = squadById.get(race.homeSquadId);
      const away = squadById.get(race.awaySquadId);
      const venue = locationById.get(race.locationId);
      const day = race.date.getDay();
      const time = race.date.getHours() * 60 + race.date.getMinutes();

      if (home === undefined || away === undefined || home.id === away.id || venue === undefined) {
        pairedUpOk = false;
      } else if (race.locationId !== home.locationId) {
        pairedUpOk = false;
      }
      if (venue !== undefined && (!Number.isInteger(race.tableNumber) || race.tableNumber < 1 || race.tableNumber > venue.numberOfTables)) {
        tablesOk = false;
      }

      const slot = venue?.slots.find(s => s.dayOfWeek === day && s.time === time);
      if (!slot || race.date.getTime() !== nthOccurrence(sundayAlignedStart, slot, race.week, blackoutKeys)?.getTime()) {
        datesOk = false;
      }
      if (blackoutKeys.has(dateKey(race.date))) datesOk = false;

      const tableKey = `${race.locationId}|${race.tableNumber}|${day}|${time}`;
      if (tableSlots.has(tableKey)) venueUsageOk = false;
      tableSlots.add(tableKey);

      for (const id of [race.homeSquadId, race.awaySquadId]) {
        if (!squadGames.has(id)) squadGames.set(id, []);
        squadGames.get(id).push({ day, locationId: race.locationId, time });
      }
    }
    for (const games of squadGames.values()) {
      if (new Set(games.map(g => g.day)).size !== 1) perWeekdayRuleOk = false;
      if (new Set(games.map(g => g.locationId)).size !== 1) perWeekdayRuleOk = false;
      if (new Set(games.map(g => g.time)).size !== games.length) perWeekdayRuleOk = false;
    }
  }

  results.push({
    key: "paired",
    label: "Every race pairs two different teams and takes place at the home team's venue.",
    passed: pairedUpOk
  });
  results.push({
    key: "tables",
    label: "Every race uses a valid table number for its venue.",
    passed: tablesOk
  });
  results.push({
    key: "dates",
    label: "Each race lands on a venue slot day and time, using the slot's Nth occurrence (N = race week), skipping blackout dates.",
    passed: datesOk
  });
  results.push({
    key: "weekday-rule",
    label: "Teams play at most one weekday, at one venue, at distinct times per week.",
    passed: perWeekdayRuleOk
  });
  results.push({
    key: "venue-usage",
    label: "Venue tables are never double-booked in a week.",
    passed: venueUsageOk
  });

  const travel = new Map(squads.map(squad => [squad.id, 0]));
  const crossOpponents = new Map(squads.map(squad => [squad.id, new Set()]));
  for (let i = 0; i < squads.length; i++) {
    for (let j = i + 1; j < squads.length; j++) {
      if (squads[i].locationId !== squads[j].locationId) {
        crossOpponents.get(squads[i].id).add(squads[j].id);
        crossOpponents.get(squads[j].id).add(squads[i].id);
      }
    }
  }
  for (const race of races) {
    const away = squadById.get(race.awaySquadId);
    if (away && race.locationId !== away.locationId) travel.set(away.id, travel.get(away.id) + 1);
  }
  const travelData = squads.map(squad => {
    const fairTarget = crossOpponents.get(squad.id).size * times / 2;
    return { squad, travel: travel.get(squad.id), fairTarget, deviation: travel.get(squad.id) - fairTarget };
  });
  results.push({
    key: "travel",
    label: "Each team travels in about half of its games against teams from other venues, within half a game.",
    passed: travelData.every(row => Math.abs(row.deviation) <= 0.5),
    data: travelData
  });

  const maxWeeks = weekNumbers[weekNumbers.length - 1] || 0;
  const seasonLength = maxWeeks >= firstWeek ? maxWeeks - firstWeek + 1 : 0;
  const gamesPerVenue = new Map();
  for (const race of races) {
    gamesPerVenue.set(race.locationId, (gamesPerVenue.get(race.locationId) || 0) + 1);
  }
  // A squad may only play on one weekday at one venue per week, but multiple times if that
  // weekday offers several slot times, so `maxGamesPerWeek` is the most slot times any venue
  // packs onto a single weekday.
  let maxGamesPerWeek = 1;
  const timesByWeekday = new Map();
  for (const location of locations) {
    for (const slot of location.slots || []) {
      const key = `${location.id}|${slot.dayOfWeek}`;
      if (!timesByWeekday.has(key)) timesByWeekday.set(key, new Set());
      timesByWeekday.get(key).add(slot.time);
    }
  }
  for (const weekdayTimes of timesByWeekday.values()) {
    maxGamesPerWeek = Math.max(maxGamesPerWeek, weekdayTimes.size);
  }
  // The floor is the strongest of:
  //  - venue capacity: a venue hosts at most numberOfTables x slots games in a week,
  //  - squad pacing: each squad plays (n - 1) x times games at its most one per weekday,
  //  - the round-robin shape: when a squad can race only once a week, a full round takes a
  //    week (n - 1 for even squads, n for odd due to byes).
  let capacityWeeks = 0;
  for (const location of locations) {
    const weeklyGames = location.numberOfTables * (location.slots || []).length;
    if (weeklyGames > 0) {
      capacityWeeks = Math.max(capacityWeeks, Math.ceil((gamesPerVenue.get(location.id) || 0) / weeklyGames));
    }
  }
  const squadWeeks = Math.ceil((squads.length - 1) * times / maxGamesPerWeek);
  const nominalWeeks = (squads.length % 2 === 1 ? squads.length : squads.length - 1) * times;
  const floorWeeks = maxGamesPerWeek === 1
    ? Math.max(capacityWeeks, nominalWeeks)
    : Math.max(capacityWeeks, squadWeeks);
  results.push({
    key: "length",
    label: "The season is as short as the venues' capacity allows.",
    passed: seasonLength === floorWeeks
  });

  return results;
}